import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  masterFetchSubscriptions,
  masterSuspendStore,
  masterActivateStore,
  logoutMaster,
  getMasterAdmin,
} from '../lib/master-api';

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;
const formatDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('ko-KR');
};

const STATUS_META = {
  trial:     { label: '체험', color: '#0EA5E9', bg: '#E0F2FE' },
  active:    { label: '활성', color: '#00BF40', bg: '#E8FAE8' },
  past_due:  { label: '미납', color: '#FF9500', bg: '#FFF5E0' },
  cancelled: { label: '해지', color: '#F04452', bg: '#FFF0F0' },
  expired:   { label: '만료', color: '#8B95A1', bg: '#F2F4F6' },
};

const MasterSubscriptionsPage = () => {
  const navigate = useNavigate();
  const admin = getMasterAdmin();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [plan, setPlan] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await masterFetchSubscriptions({ status, plan });
      setSubs(data.subscriptions || []);
    } finally {
      setLoading(false);
    }
  }, [status, plan]);

  useEffect(() => { load(); }, [load]);

  const totalMrr = subs
    .filter((s) => s.status === 'active')
    .reduce((sum, s) => {
      const price = s.billing_cycle === 'yearly'
        ? Math.round((s.yearly_price || s.monthly_price * 12) / 12)
        : s.monthly_price;
      return sum + (price || 0);
    }, 0);

  return (
    <div style={{ minHeight: '100vh', background: '#F7F9FC' }}>
      {/* Top bar */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid var(--border)',
        padding: '14px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36,
            background: 'linear-gradient(135deg, #BF9B30 0%, #806413 100%)',
            borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
              <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" />
            </svg>
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>MASTER CONSOLE</div>
        </div>
        <nav style={{ display: 'flex', gap: 6 }}>
          {[
            { label: '대시보드', to: '/master' },
            { label: '매장', to: '/master/stores' },
            { label: '구독', to: '/master/subscriptions' },
          ].map((item) => (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              style={{
                padding: '8px 14px',
                background: window.location.pathname === item.to ? 'var(--primary-light)' : 'transparent',
                color: window.location.pathname === item.to ? 'var(--primary)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: 8,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{admin?.name || '관리자'}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{admin?.email}</div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={logoutMaster}>로그아웃</button>
        </div>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: '1.55rem', fontWeight: 800, letterSpacing: '-0.5px' }}>구독 관리</h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.88rem', marginTop: 4 }}>
            전체 {subs.length}개 · 활성 MRR {formatWon(totalMrr)}
          </p>
        </div>

        {/* Filters */}
        <div style={{
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input" style={{ minWidth: 140 }}>
            <option value="">전체 상태</option>
            <option value="trial">체험</option>
            <option value="active">활성</option>
            <option value="past_due">미납</option>
            <option value="cancelled">해지</option>
            <option value="expired">만료</option>
          </select>
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="form-input" style={{ minWidth: 140 }}>
            <option value="">전체 플랜</option>
            <option value="plan-free">무료 체험</option>
            <option value="plan-basic">베이직</option>
            <option value="plan-pro">프로</option>
            <option value="plan-enterprise">엔터프라이즈</option>
          </select>
        </div>

        {/* Table */}
        <div style={{
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 14,
          overflow: 'hidden',
        }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <div className="spinner spinner-dark" style={{ width: 28, height: 28, borderWidth: 3 }} />
            </div>
          ) : subs.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-tertiary)' }}>구독이 없습니다</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#FAFBFD', borderBottom: '1px solid var(--border)' }}>
                    <th style={thStyle}>매장</th>
                    <th style={thStyle}>플랜</th>
                    <th style={thStyle}>주기</th>
                    <th style={thStyle}>상태</th>
                    <th style={thStyle}>시작일</th>
                    <th style={thStyle}>다음 결제</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>MRR</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => {
                    const meta = STATUS_META[s.status] || STATUS_META.trial;
                    const mrr = s.billing_cycle === 'yearly'
                      ? Math.round((s.yearly_price || s.monthly_price * 12) / 12)
                      : s.monthly_price;
                    return (
                      <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 700 }}>{s.store_name}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{s.owner_email}</div>
                        </td>
                        <td style={tdStyle}>{s.plan_name}</td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontSize: '0.72rem',
                            background: 'var(--bg)',
                            color: 'var(--text-secondary)',
                            fontWeight: 600,
                          }}>
                            {s.billing_cycle === 'yearly' ? '연간' : '월간'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 999,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            background: meta.bg,
                            color: meta.color,
                          }}>
                            {meta.label}
                          </span>
                        </td>
                        <td style={tdStyle}>{formatDate(s.started_at)}</td>
                        <td style={tdStyle}>{formatDate(s.next_billing_at)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>
                          {s.status === 'active' ? formatWon(mrr) : '-'}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {(s.status === 'active' || s.status === 'trial') ? (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={async () => {
                                if (window.confirm('일시정지하시겠습니까?')) {
                                  await masterSuspendStore(s.store_id);
                                  load();
                                }
                              }}
                            >
                              일시정지
                            </button>
                          ) : (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={async () => {
                                await masterActivateStore(s.store_id);
                                load();
                              }}
                            >
                              활성화
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '14px 20px',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const tdStyle = {
  padding: '14px 20px',
  fontSize: '0.85rem',
  color: 'var(--text)',
};

export default MasterSubscriptionsPage;
